import type { CheckItem, ClientEvent, DraftState, Segment } from './types';

function cycleSwap(segment: Segment): Segment {
  const { swap } = segment;

  if (!swap || swap.options.length < 2) {
    return segment;
  }

  const index = (swap.index + 1) % swap.options.length;

  return {
    ...segment,
    text: swap.before + swap.options[index] + swap.after,
    swap: { ...swap, index },
  };
}

function updateSegment(
  state: DraftState,
  segmentId: string,
  update: (segment: Segment) => Segment
): DraftState {
  return {
    ...state,
    segments: state.segments.map((segment) =>
      segment.id === segmentId ? update(segment) : segment
    ),
  };
}

/**
 * The must-fix suggestions left out of the reply, as the server will count
 * them, so the footer's count moves with the tap. The server's next view
 * corrects any item the expert has already acknowledged.
 */
function pendingMustFixAfter(state: DraftState, segment: Segment, struck: boolean): CheckItem[] {
  const rest = state.pendingMustFix.filter((item) => item.segmentId !== segment.id);

  if (!struck || segment.severity !== 'must_fix') {
    return rest;
  }

  const listed = state.pendingMustFix.find((item) => item.segmentId === segment.id);

  return [...rest, listed ?? { segmentId: segment.id, label: segment.text, severity: 'must_fix' }];
}

/**
 * Folds one tap into the draft before the server answers, so the screen moves
 * at once. It mirrors the server's fold: a segment is in the reply exactly
 * when it is not struck, suggestions included, and a swap cycles its detail.
 *
 * Only rendering reads the result. The server's next view replaces it, and
 * `composedText` is left as the server last sent it, because the app never
 * sends text it composed.
 */
export function applyLocalEvent(state: DraftState, event: ClientEvent): DraftState {
  switch (event.type) {
    case 'segment_struck':
    case 'segment_restored': {
      const struck = event.type === 'segment_struck';

      const target = state.segments.find((segment) => segment.id === event.segmentId);

      if (!target) {
        return state;
      }

      const next = updateSegment(state, event.segmentId, (segment) => ({
        ...segment,
        struck,
        included: !struck,
      }));

      return { ...next, pendingMustFix: pendingMustFixAfter(state, target, struck) };
    }
    case 'swap_cycled':
      return updateSegment(state, event.segmentId, cycleSwap);
  }
}

export function applyLocalEvents(state: DraftState, events: ClientEvent[]): DraftState {
  return events.reduce(applyLocalEvent, state);
}
